import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { Switch } from "@aurumiq/ui/components/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Field, TextAreaField, TextField } from "@/components/field";
import type { AdminPlan } from "@/lib/types";
import { usePutPlan } from "./queries";

const schema = z.object({
  name: z.string().trim().min(1, "Required").max(60),
  tier: z.string().trim().min(1, "Required").max(40),
  seats: z.coerce.number().int().min(0),
  features: z.string(),
  price_month: z.coerce.number().min(0),
  price_year: z.coerce.number().min(0),
  currency: z.string().trim().length(3, "ISO 4217 code").toUpperCase(),
  active: z.boolean(),
  sort_order: z.coerce.number().int(),
  reason: z.string().trim(),
});
type Values = z.input<typeof schema>;
type Parsed = z.output<typeof schema>;

export function PlanDialog({
  plan,
  onOpenChange,
}: {
  plan: AdminPlan | null;
  onOpenChange: (open: boolean) => void;
}) {
  const put = usePutPlan();
  const form = useForm<Values, unknown, Parsed>({
    resolver: zodResolver(schema),
    values: {
      name: plan?.name ?? "",
      tier: plan?.tier ?? "",
      seats: plan?.seats ?? 1,
      features: (plan?.features ?? []).join("\n"),
      price_month: plan?.price_month ?? 0,
      price_year: plan?.price_year ?? 0,
      currency: plan?.currency ?? "USD",
      active: plan?.active ?? true,
      sort_order: plan?.sort_order ?? 0,
      reason: "",
    },
  });
  const err = (k: keyof Values) => form.formState.errors[k]?.message;
  const onSubmit = form.handleSubmit(async (v) => {
    if (!plan) return;
    await put.mutateAsync({
      code: plan.code,
      name: v.name,
      tier: v.tier,
      seats: v.seats,
      features: v.features
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      price_month: v.price_month,
      price_year: v.price_year,
      currency: v.currency,
      active: v.active,
      sort_order: v.sort_order,
      reason: v.reason || undefined,
    });
    onOpenChange(false);
  });
  return (
    <Dialog open={plan !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              Edit plan <span className="font-mono">{plan?.code}</span>
            </DialogTitle>
            <DialogDescription>
              Prices are in major units. Run “Sync to Stripe” afterwards to push product / price
              changes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField label="Name" error={err("name")} {...form.register("name")} />
            <TextField label="Tier" error={err("tier")} {...form.register("tier")} />
            <TextField
              label="Seats"
              type="number"
              min={0}
              error={err("seats")}
              {...form.register("seats")}
            />
            <TextField
              label="Currency"
              maxLength={3}
              error={err("currency")}
              {...form.register("currency")}
            />
            <TextField
              label="Price / month"
              type="number"
              min={0}
              step="0.01"
              error={err("price_month")}
              {...form.register("price_month")}
            />
            <TextField
              label="Price / year"
              type="number"
              min={0}
              step="0.01"
              error={err("price_year")}
              {...form.register("price_year")}
            />
            <TextField
              label="Sort order"
              type="number"
              error={err("sort_order")}
              {...form.register("sort_order")}
            />
            <Field label="Active" htmlFor="plan-active">
              <div className="flex h-9 items-center">
                <Controller
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <Switch
                      id="plan-active"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </Field>
          </div>
          <TextAreaField
            label="Features (one per line)"
            rows={5}
            error={err("features")}
            {...form.register("features")}
          />
          <TextAreaField
            label="Reason (optional)"
            placeholder="Recorded in the audit log."
            {...form.register("reason")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={put.isPending}>
              {put.isPending ? "Saving…" : "Save plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
